import { useState, type MouseEvent } from 'react';
import { Box, IconButton, Popover, Tooltip, Typography } from '@mui/material';
import { RiCloseLine, RiFileTextLine, RiQuestionLine } from '@remixicon/react';
import { useTranslation } from 'react-i18next';
import { BuzonNucStatusEnum } from '@/shared/types/interfaces/BuzonJudicial.interface';
import { statusConfig } from './BuzonEstadoChip';

interface BuzonAyudaPopoverProps {
  /** Tamaño máximo del documento de justificación, para que el texto no se desfase de la validación real. */
  maxFileSizeMb: number;
}

const POPOVER_ID = 'buzon-ayuda-popover';
const TITLE_ID = 'buzon-ayuda-popover-title';

const PRIMARY = '#0050DA';

/** Estados que se explican en la leyenda, en el orden en que el usuario los recorre. Color y etiqueta
 *  salen de `statusConfig` para que coincidan con el chip de la columna Estado; solo "Habilitado" usa
 *  una etiqueta genérica porque el chip la divide en "por caso" / "por instancia". */
const ESTADOS_LEYENDA: { estado: BuzonNucStatusEnum; labelKey?: string; defaultLabel?: string; descKey: string; defaultDesc: string }[] = [
  { estado: BuzonNucStatusEnum.NO_HABILITADO, descKey: 'buzonJudicial.help.statusNoHabilitado', defaultDesc: 'Aún no recibe notificaciones.' },
  { estado: BuzonNucStatusEnum.PENDIENTE_FIRMA, descKey: 'buzonJudicial.help.statusPendienteFirma', defaultDesc: 'Esperando su firma digital.' },
  {
    estado: BuzonNucStatusEnum.HABILITADO,
    labelKey: 'buzonJudicial.status.habilitado',
    defaultLabel: 'Habilitado',
    descKey: 'buzonJudicial.help.statusHabilitado',
    defaultDesc: 'Recibe notificaciones en su buzón.',
  },
  { estado: BuzonNucStatusEnum.PENDIENTE_DESHABILITAR, descKey: 'buzonJudicial.help.statusPendienteDeshabilitar', defaultDesc: 'Solicitud en revisión.' },
  { estado: BuzonNucStatusEnum.PENDIENTE_REHABILITAR, descKey: 'buzonJudicial.help.statusPendienteHabilitar', defaultDesc: 'Solicitud en revisión.' },
  { estado: BuzonNucStatusEnum.DESHABILITADO, descKey: 'buzonJudicial.help.statusDeshabilitado', defaultDesc: 'Ya no recibe notificaciones.' },
];

const sectionTitleSx = {
  m: 0,
  mb: 1.25,
  fontSize: '0.7rem',
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: '#94a3b8',
} as const;

const bodyTextSx = { m: 0, fontSize: '0.82rem', lineHeight: 1.5, color: '#475569' } as const;

/**
 * Botón de ayuda (RiQuestionLine) que despliega un resumen breve de cómo funciona el Buzón Judicial:
 * pasos para habilitar un NUC, diferencia entre habilitar por caso o por instancia, significado de cada
 * estado y requisitos para deshabilitar o volver a habilitar. Solo informativo, no altera el flujo.
 */
const BuzonAyudaPopover = ({ maxFileSizeMb }: BuzonAyudaPopoverProps) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const pasos = [
    t('buzonJudicial.help.step1', 'Elija el NUC y pulse Habilitar, por caso o por instancia.'),
    t('buzonJudicial.help.step2', 'Firme la autorización digital que recibirá en su correo.'),
    t('buzonJudicial.help.step3', 'Listo: las notificaciones y citaciones de ese NUC llegarán a su Buzón Judicial.'),
  ];

  const modos = [
    {
      titulo: t('buzonJudicial.help.modeCaseTitle', 'Por caso'),
      descripcion: t('buzonJudicial.help.modeCaseDesc', 'Cubre todas las instancias del NUC.'),
    },
    {
      titulo: t('buzonJudicial.help.modeInstanceTitle', 'Por instancia'),
      descripcion: t('buzonJudicial.help.modeInstanceDesc', 'Solo las categorías de tribunal que elija.'),
    },
  ];

  return (
    <>
      <Tooltip title={open ? '' : t('buzonJudicial.help.trigger', '¿Cómo funciona el Buzón Judicial?')} arrow>
        <IconButton
          size="small"
          onClick={handleOpen}
          aria-label={t('buzonJudicial.help.trigger', '¿Cómo funciona el Buzón Judicial?')}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-controls={open ? POPOVER_ID : undefined}
          sx={{
            flexShrink: 0,
            width: 34,
            height: 34,
            color: open ? PRIMARY : '#64748b',
            backgroundColor: open ? '#eff6ff' : 'transparent',
            transition: 'color 160ms ease, background-color 160ms ease',
            '&:hover': { color: PRIMARY, backgroundColor: '#eff6ff' },
            '&.Mui-focusVisible': { outline: `2px solid ${PRIMARY}`, outlineOffset: 1 },
          }}
        >
          <RiQuestionLine size={19} />
        </IconButton>
      </Tooltip>

      <Popover
        id={POPOVER_ID}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            role: 'dialog',
            'aria-labelledby': TITLE_ID,
            elevation: 0,
            sx: {
              mt: 1,
              width: 380,
              maxWidth: 'calc(100vw - 32px)',
              maxHeight: 'min(72vh, 600px)',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: '14px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 18px 40px -12px rgba(15,23,42,0.22), 0 2px 6px rgba(15,23,42,0.05)',
              overflow: 'hidden',
            },
          },
        }}
      >
        {/* ── Encabezado ── */}
        <Box sx={{ px: 2.5, pt: 2.25, pb: 2, borderBottom: '1px solid #f1f5f9', flexShrink: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '10px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#eff6ff',
                  color: PRIMARY,
                  flexShrink: 0,
                }}
              >
                <RiQuestionLine size={18} />
              </Box>
              <Typography id={TITLE_ID} component="h3" sx={{ m: 0, fontSize: '0.95rem', fontWeight: 600, color: '#0f172a' }}>
                {t('buzonJudicial.help.title', '¿Cómo funciona el Buzón Judicial?')}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={handleClose}
              aria-label={t('actions.close', 'Cerrar')}
              sx={{ mt: -0.5, mr: -1, color: '#94a3b8', '&:hover': { color: '#475569', backgroundColor: '#f1f5f9' } }}
            >
              <RiCloseLine size={18} />
            </IconButton>
          </Box>
          <Typography component="p" sx={{ ...bodyTextSx, mt: 1.25 }}>
            {t(
              'buzonJudicial.help.intro',
              'Reciba en un solo lugar las notificaciones y citaciones de sus casos. Solo los NUCs habilitados envían notificaciones a su buzón.',
            )}
          </Typography>
        </Box>

        {/* ── Contenido ── */}
        <Box sx={{ px: 2.5, py: 2, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <section>
            <Typography component="h4" sx={sectionTitleSx}>
              {t('buzonJudicial.help.stepsTitle', 'Habilitar un NUC')}
            </Typography>
            <Box component="ol" sx={{ m: 0, p: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {pasos.map((paso, index) => (
                <Box component="li" key={index} sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
                  <Box
                    component="span"
                    aria-hidden
                    sx={{
                      width: 20,
                      height: 20,
                      mt: '1px',
                      borderRadius: '50%',
                      flexShrink: 0,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      color: PRIMARY,
                      backgroundColor: '#eff6ff',
                    }}
                  >
                    {index + 1}
                  </Box>
                  <Typography component="span" sx={bodyTextSx}>{paso}</Typography>
                </Box>
              ))}
            </Box>
          </section>

          <section>
            <Typography component="h4" sx={sectionTitleSx}>
              {t('buzonJudicial.help.modesTitle', '¿Por caso o por instancia?')}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
              {modos.map((modo) => (
                <Box key={modo.titulo} sx={{ p: 1.25, borderRadius: '10px', backgroundColor: '#f8fafc', border: '1px solid #f1f5f9' }}>
                  <Typography component="p" sx={{ m: 0, mb: 0.25, fontSize: '0.8rem', fontWeight: 600, color: '#0f172a' }}>
                    {modo.titulo}
                  </Typography>
                  <Typography component="p" sx={{ ...bodyTextSx, fontSize: '0.76rem', lineHeight: 1.45 }}>
                    {modo.descripcion}
                  </Typography>
                </Box>
              ))}
            </Box>
          </section>

          <section>
            <Typography component="h4" sx={sectionTitleSx}>
              {t('buzonJudicial.help.statusTitle', 'Estados')}
            </Typography>
            <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 0.9 }}>
              {ESTADOS_LEYENDA.map((item) => (
                <Box component="li" key={item.estado} sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Box
                    component="span"
                    aria-hidden
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      flexShrink: 0,
                      backgroundColor: statusConfig[item.estado].color,
                      transform: 'translateY(-1px)',
                    }}
                  />
                  <Typography component="span" sx={{ ...bodyTextSx, fontSize: '0.8rem' }}>
                    <Box component="span" sx={{ fontWeight: 600, color: '#0f172a' }}>
                      {t(item.labelKey ?? statusConfig[item.estado].labelKey, {
                        defaultValue: item.defaultLabel ?? statusConfig[item.estado].defaultLabel,
                      })}
                    </Box>
                    {' · '}
                    {t(item.descKey, item.defaultDesc)}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Typography component="p" sx={{ ...bodyTextSx, mt: 1.25, fontSize: '0.76rem', color: '#64748b' }}>
              {t('buzonJudicial.help.statusCancelHint', 'Mientras una solicitud esté pendiente, puede cancelarla desde la columna Acciones.')}
            </Typography>
          </section>

          <Box sx={{ display: 'flex', gap: 1.25, p: 1.5, borderRadius: '10px', backgroundColor: '#f8fafc', border: '1px solid #f1f5f9' }}>
            <Box sx={{ color: PRIMARY, flexShrink: 0, mt: '1px', display: 'inline-flex' }}>
              <RiFileTextLine size={16} />
            </Box>
            <Box>
              <Typography component="p" sx={{ m: 0, mb: 0.5, fontSize: '0.8rem', fontWeight: 600, color: '#0f172a' }}>
                {t('buzonJudicial.help.changeTitle', 'Deshabilitar o volver a habilitar')}
              </Typography>
              <Typography component="p" sx={{ ...bodyTextSx, fontSize: '0.76rem', lineHeight: 1.5 }}>
                {t('buzonJudicial.help.changeDesc', {
                  size: maxFileSizeMb,
                  defaultValue: `Adjunte una comunicación en PDF (máx. ${maxFileSizeMb} MB) dirigida a la Unidad de Registro de Usuarios, firmada o sellada. Las notificaciones realizadas mientras la solicitud está en trámite siguen siendo válidas.`,
                })}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default BuzonAyudaPopover;
